import React from "react";
import ReactSelect from "react-select";
import { useTheme } from "../../contexts/ThemeContext";
// import "./FilterBarDatePicker.css";

interface FilterOption {
  label: string;
  value: string;
}

interface FilterBarProps {
  filters: {
    label: string;
    options?: FilterOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    type?: "select" | "date";
    /** When true, the first empty option is omitted (e.g. required two-option selects). */
    hideEmptyOption?: boolean;
    /** Applied to the filter column wrapper (e.g. `ml-auto` to pin to the right). */
    wrapperClassName?: string;
  }[];
  className?: string;
}

const FilterBar: React.FC<FilterBarProps> = ({ filters, className = "" }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-sm backdrop-blur-sm dark:border-slate-700/60 dark:bg-[#1C202F]/50 ${className}`}
    >
      <div className="grid w-full grid-cols-1 gap-2.5 sm:gap-3 md:grid-cols-2 xl:grid-cols-3">
        {filters.map((filter, index) => (
          <div
            key={index}
            className={`relative z-0 flex min-w-0 flex-col space-y-1 rounded-xl border border-slate-200/80 bg-slate-50 px-3 py-2 dark:border-transparent dark:bg-[#1C202F]/40 sm:px-4 sm:py-2.5 ${filter.wrapperClassName ?? ""}`}
          >
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 sm:text-[11px]">
              {filter.label}
            </label>
            {filter.type === "date" ? (
              <input
                type="date"
                value={filter.value}
                onChange={(e) => filter.onChange(e.target.value)}
                onClick={(e) => {
                  // Some browsers (Chrome/Edge) support programmatic opening.
                  const el = e.currentTarget as unknown as { showPicker?: () => void };
                  el.showPicker?.();
                }}
                className="pointer-events-auto h-9 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 [color-scheme:light] placeholder:text-slate-400 focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700/60 dark:bg-[#0b0d14] dark:text-slate-100 dark:placeholder:text-slate-500 dark:[color-scheme:dark]"
              />
            ) : (
              <div className="w-full min-w-0">
                <ReactSelect
                  inputId={`filter-${filter.label}-${index}`}
                  instanceId={`filter-${filter.label}-${index}`}
                  className="w-full"
                  classNamePrefix="filterbar-select"
                  options={
                    !filter.hideEmptyOption
                      ? [
                          {
                            label: filter.placeholder || `Select ${filter.label}`,
                            value: "",
                          },
                          ...(filter.options ?? []),
                        ]
                      : filter.options ?? []
                  }
                  value={
                    (!filter.hideEmptyOption
                      ? [
                          {
                            label: filter.placeholder || `Select ${filter.label}`,
                            value: "",
                          },
                          ...(filter.options ?? []),
                        ]
                      : filter.options ?? []
                    ).find((o) => o.value === filter.value) ?? null
                  }
                  placeholder={filter.placeholder || `Select ${filter.label}`}
                  isSearchable
                  isClearable={!filter.hideEmptyOption}
                  menuPlacement="auto"
                  menuPosition="fixed"
                  menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                  onChange={(selected) => {
                    const nextValue = (selected as { value: string } | null)?.value ?? "";
                    filter.onChange(nextValue);
                  }}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: 36,
                      height: 36,
                      backgroundColor: isDarkMode ? "#0b0d14" : "#F8FAFC",
                      borderColor: state.isFocused ? "#3B82F6" : isDarkMode ? "#334155" : "#E5E7EB",
                      borderWidth: 1,
                      borderRadius: 8,
                      boxShadow: state.isFocused
                        ? "0 0 0 2px rgba(59,130,246,0.25)"
                        : "none",
                      cursor: "pointer",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: "0 12px",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: isDarkMode ? "#64748B" : "#64748B",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: isDarkMode ? "#E5E7EB" : "#0F172A",
                    }),
                    input: (base) => ({
                      ...base,
                      color: isDarkMode ? "#E5E7EB" : "#0F172A",
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: isDarkMode ? "#161b2b" : "#FFFFFF",
                      borderRadius: 12,
                      border: `1px solid ${isDarkMode ? "#334155" : "#E5E7EB"}`,
                      overflow: "hidden",
                      zIndex: 9999,
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    menuList: (base) => ({
                      ...base,
                      padding: 8,
                      maxHeight: 280,
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: 10,
                      backgroundColor: state.isSelected
                        ? isDarkMode
                          ? "rgba(59,130,246,0.35)"
                          : "rgba(37,99,235,0.15)"
                        : state.isFocused
                          ? isDarkMode
                            ? "rgba(59,130,246,0.18)"
                            : "rgba(37,99,235,0.10)"
                          : "transparent",
                      color: isDarkMode ? "#E5E7EB" : "#0F172A",
                      cursor: "pointer",
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      backgroundColor: isDarkMode ? "#334155" : "#E5E7EB",
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: isDarkMode ? "#94A3B8" : "#64748B",
                      ":hover": { color: isDarkMode ? "#E5E7EB" : "#0F172A" },
                    }),
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FilterBar;
